import { useMemo } from 'react';
import { buildGraph, cardAddress, layout, relate, type Address, type Graph, type Kin, type KinPack, type Layout } from '@lineage/core';
import type { Snapshot } from '../../lib/api';
import { useKinPack } from '../../lib/prefs';

export interface TreeModel {
  graph: Graph;
  layout: Layout;
  /** How everyone is related to `meId`, worked out once per viewer (PRD 7.2). */
  kins: Map<string, Kin>;
  /** Relationship label of everyone as seen from `meId`, in the chosen words. */
  labels: Map<string, string>;
  /** What to call them (PRD 7.2.4): every usual form of address, most usual first; empty when by name. */
  addresses: Map<string, Address>;
  /** The one form shown on a card ("Chachaji", "Deepeshbhai / Deepesh"), or '' when by name. */
  calls: Map<string, string>;
  /** The relationship words in use (labels, generation captions, "how you're related"). */
  pack: KinPack;
}

/** Graph and layout are memoised by tree version; relationships by viewer; labels by word set. */
export function useTreeModel(snap: Pick<Snapshot, 'people' | 'parentLinks' | 'partnerships' | 'version'>, meId: string | null): TreeModel {
  const pack = useKinPack();
  const { graph, lay } = useMemo(() => {
    const g = buildGraph(snap.people, snap.parentLinks, snap.partnerships);
    return { graph: g, lay: layout(g) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap.version, snap.people.length, snap.people]);
  const kins = useMemo(() => {
    const m = new Map<string, Kin>();
    if (meId && graph.people.has(meId)) for (const id of graph.people.keys()) m.set(id, relate(graph, meId, id));
    return m;
  }, [graph, meId]);
  const labels = useMemo(() => new Map([...kins].map(([id, k]) => [id, pack.label(k)])), [kins, pack]);
  const addresses = useMemo(() => new Map([...kins].map(([id, k]) => [id, pack.address(k, graph.people.get(id)?.firstName)])), [kins, pack, graph]);
  const calls = useMemo(() => new Map([...addresses].map(([id, a]) => [id, cardAddress(a, labels.get(id) ?? '', graph.people.get(id)?.firstName)])), [addresses, labels, graph]);
  return { graph, layout: lay, kins, labels, addresses, calls, pack };
}
